import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(2),
    minHeight: 92,
  },
  cardNumber: {
    fontWeight: 700,
    marginTop: theme.spacing(1),
  },
  table: {
    minWidth: 1100,
  },
}));

const today = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const formatDateTime = value => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch (err) {
    return value;
  }
};

const toCsvValue = value => {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const UserAttendanceReport = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(firstDayOfMonth());
  const [endDate, setEndDate] = useState(today());
  const [userId, setUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    totalTickets: 0,
    openTickets: 0,
    pendingTickets: 0,
    closedTickets: 0,
    groupTickets: 0,
    totalMessages: 0,
    sentMessages: 0,
    receivedMessages: 0,
  });

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/user-attendance", {
        params: {
          startDate,
          endDate,
          userId: userId || undefined,
        },
      });
      setRows(data.data || []);
      setTotals(data.totals || {});
      setUsers(data.users || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const csvContent = useMemo(() => {
    const header = [
      "Usuario",
      "Email",
      "Total atendimentos",
      "Em atendimento",
      "Aguardando",
      "Fechados",
      "Grupos",
      "Mensagens total",
      "Mensagens enviadas",
      "Mensagens recebidas",
      "Primeiro atendimento",
      "Ultima atividade",
    ];
    const body = rows.map(row => [
      row.userName,
      row.userEmail,
      row.totalTickets,
      row.openTickets,
      row.pendingTickets,
      row.closedTickets,
      row.groupTickets,
      row.totalMessages,
      row.sentMessages,
      row.receivedMessages,
      formatDateTime(row.firstTicketAt),
      formatDateTime(row.lastActivityAt),
    ]);
    return [header, ...body].map(line => line.map(toCsvValue).join(";")).join("\n");
  }, [rows]);

  const exportCsv = () => {
    const blob = new Blob([`\ufeff${csvContent}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `relatorio-atendimento-por-usuario-${startDate}-${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Atendimento por Usuário</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={loadReport}
            disabled={loading}
          >
            Filtrar
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={exportCsv}
            disabled={loading || rows.length === 0}
          >
            Exportar CSV
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2} className={classes.filters}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data inicial"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data final"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Usuário</InputLabel>
              <Select
                value={userId}
                onChange={e => setUserId(e.target.value)}
                label="Usuário"
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.filters}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography variant="body2" color="textSecondary">Total de atendimentos</Typography>
              <Typography variant="h4" color="primary" className={classes.cardNumber}>{totals.totalTickets || 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography variant="body2" color="textSecondary">Fechados</Typography>
              <Typography variant="h4" color="primary" className={classes.cardNumber}>{totals.closedTickets || 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography variant="body2" color="textSecondary">Em atendimento</Typography>
              <Typography variant="h4" color="primary" className={classes.cardNumber}>{totals.openTickets || 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.card} variant="outlined">
              <Typography variant="body2" color="textSecondary">Mensagens enviadas</Typography>
              <Typography variant="h4" color="primary" className={classes.cardNumber}>{totals.sentMessages || 0}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <TableContainer>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuário</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Open</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Closed</TableCell>
                <TableCell align="right">Grupos</TableCell>
                <TableCell align="right">Msg. total</TableCell>
                <TableCell align="right">Enviadas</TableCell>
                <TableCell align="right">Recebidas</TableCell>
                <TableCell>Primeiro atendimento</TableCell>
                <TableCell>Última atividade</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.userId}>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell>{row.userEmail}</TableCell>
                  <TableCell align="right">{row.totalTickets}</TableCell>
                  <TableCell align="right">{row.openTickets}</TableCell>
                  <TableCell align="right">{row.pendingTickets}</TableCell>
                  <TableCell align="right">{row.closedTickets}</TableCell>
                  <TableCell align="right">{row.groupTickets}</TableCell>
                  <TableCell align="right">{row.totalMessages}</TableCell>
                  <TableCell align="right">{row.sentMessages}</TableCell>
                  <TableCell align="right">{row.receivedMessages}</TableCell>
                  <TableCell>{formatDateTime(row.firstTicketAt)}</TableCell>
                  <TableCell>{formatDateTime(row.lastActivityAt)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    {loading ? "Carregando relatório..." : "Nenhum atendimento encontrado para os filtros selecionados."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default UserAttendanceReport;
